'use client';

import { useUser } from './User';
import { useAuthProvider } from '@modules/auth/providers/AuthProvider/AuthProvider';
import { useGetWorkspace } from '@modules/workspaces/api/useGetWorkspace';
import useFetcher from 'app/_utils/data/useFetcher';

import { Toast } from '@capra/core';
import React, { useMemo, createContext, useContext, useState, useEffect } from 'react';
import { AccountState } from 'shared/src/account/types';
import { Workspace } from 'shared/src/generated/openapi/v2/types';
import { WorkerGroup } from 'shared/src/models/WorkerGroup';
import { TeamCompact } from 'shared/src/teams/types';
import { DEFAULT_WORKERGROUP_NAME } from 'shared/src/workspaces/constants';
import useSWR, { KeyedMutator } from 'swr';

const PROVISION_STATE_FETCH_INTERVAL = 60000;
const HIBERNATION_STATE_FETCH_INTERVAL = 5000;

type WithPathName = {
  pathname?: string;
};

type WithTeams = {
  teams?: TeamCompact[];
};

export type WorkspaceWithPathName = Workspace &
  WithPathName &
  {
    versionFull?: string;
    insightsEnabled?: boolean;
    quality?: string;
    productBuildSubscription?: {
      channel: string;
      version: string;
    };
    productBuildIdentifier?: {
      channel: string;
      version: string;
      build: string;
      quality: string;
    };
    productBuildVersionFull?: string;
    qualityDetail?: string;
    blockCriblUpdatesUntil?: string;
  } &
  WithTeams;

export type IWorkspaceContext = WorkspaceWithPathName & {
  workspaces: WorkspaceWithPathName[];
  workerGroups: WorkerGroup[];
  isWorkspaceLoading: boolean;
  selectedWorkerGroup: WorkerGroup;
  versionFull: string;
  setWorkerGroup: (name: string) => void;
  workspacesError?: Error;
  workerGroupsError?: Error;
  mutateWorkspaces: KeyedMutator<Workspace[]>;
  mutateWorkerGroups: KeyedMutator<WorkerGroup[]>;
  workspaceError: unknown;
  error?: Error;
  setWorkspaceId: (id: string) => void;
};

const WorkspaceContext = createContext<IWorkspaceContext>({} as IWorkspaceContext);

export function useWorkspace(): IWorkspaceContext {
  const context = useContext(WorkspaceContext);

  if (context === undefined) {
    throw new Error('useWorkspace must be used within a WorkspaceProvider');
  }

  return context;
}

export const WorkspaceProvider = ({ children }: { children: React.ReactNode }) => {
  const { authorizedOrganizationId } = useAuthProvider();
  const { updateUserPreferences, userPreferences } = useUser();
  const { fetcher } = useFetcher();
  const [workspaceId, setWorkspaceId] = useState<string | null>(
    (authorizedOrganizationId && userPreferences?.[authorizedOrganizationId]?.currentWorkspaceId) || null,
  );
  const [isWorkspaceLoading, setIsWorkspaceLoading] = useState(true);

  const {
    data: workspaces,
    error: workspacesError,
    mutate: mutateWorkspaces,
  } = useSWR<WorkspaceWithPathName[]>(
    authorizedOrganizationId ? `${process.env.ZEUS_API_URL}/v2/organizations/${authorizedOrganizationId}/workspaces` : null,
    fetcher,
    {
      revalidateOnFocus: false,
    },
  );

  const {
    data: workerGroupsData,
    mutate: mutateWorkerGroups,
    error: workspaceError,
  } = useSWR<WorkerGroup[]>(
    authorizedOrganizationId && workspaceId
      ? `${process.env.ZEUS_API_URL}/v2/organizations/${authorizedOrganizationId}/workspaces/${workspaceId}/workergroups`
      : null,
    fetcher,
    {
      revalidateOnFocus: false,
    },
  );

  const { data: singleWorkspaceData } = useGetWorkspace({
    organizationId: authorizedOrganizationId,
    workspaceId,
  });

  const workerGroups = useMemo(() => {
    if (!workerGroupsData) {
      return [];
    }

    return workerGroupsData.filter((wg) => wg.kind !== 'lake_access');
  }, [workerGroupsData]);

  const [selectedWorkerGroup, setSelectedWorkerGroup] = useState({} as WorkerGroup);

  const setWorkerGroup = (workerGroupName: string): void => {
    const foundWorkerGroup = workerGroups?.find((workerGroup) => workerGroup.name === workerGroupName);

    if (foundWorkerGroup) {
      setSelectedWorkerGroup(foundWorkerGroup);
    } else {
      Toast.error('Unable to set worker group.', { position: 'bottom-right' });
      console.error(`Worker group ${workerGroupName} not found`, { workerGroups });
    }
  };

  useEffect(() => {
    if (!Array.isArray(workerGroups) || workerGroups.length === 0) {
      // This can happen because authorizedOrganizationId is null,
      // or if there are no worker groups returned from the API
      return;
    }

    // TODO: Track selected worker groups by workspace
    setSelectedWorkerGroup({} as WorkerGroup);
  }, [workspaceId, workerGroups]);

  // @TODO: move useState to the top of the component
  const [workspace, setWorkspace] = useState<IWorkspaceContext>(() => ({
    workspaces: [] as Workspace[],
    selectedWorkerGroup: {} as WorkerGroup,
    setWorkerGroup,
    mutateWorkspaces,
    mutateWorkerGroups,
    workspaceError,
    workspacesError,
    isWorkspaceLoading,
    setWorkspaceId,
    workerGroups: [] as WorkerGroup[],
    versionFull: '',
  } as IWorkspaceContext));

  useEffect(() => {
    if (workspaceId && authorizedOrganizationId) {
      updateUserPreferences({
        [authorizedOrganizationId]: {
          currentWorkspaceId: workspaceId,
        },
      });
    }
  }, [authorizedOrganizationId, workspaceId, updateUserPreferences]);

  useEffect(() => {
    if (!Array.isArray(workspaces) || workspaces.length === 0) {
      // This can happen because authorizedOrganizationId is null,
      // or if no workspaces are returned from the API because the user has access to no workspaces
      return;
    }

    if (!workspaceId) {
      const currentWorkspaceId = authorizedOrganizationId &&
        userPreferences?.[authorizedOrganizationId]?.currentWorkspaceId;

      if (currentWorkspaceId && workspaces.some((ws) => ws.name === currentWorkspaceId)) {
        setWorkspaceId(currentWorkspaceId);
      } else {
        setWorkspaceId(workspaces[0].name);
      }
    } else if (!workspaces.some((ws) => ws.name === workspaceId)) {
      // if workspaceId is not in the workspaces array, set it to the first workspace
      setWorkspaceId(workspaces[0].name);
    }

    // update workspace to contain an additional attribute "pathname"
    workspaces.forEach((ws) => {
      ws.pathname = `/organizations/${authorizedOrganizationId}/workspaces/${ws.name}`;
    });

    const currentWorkspace = workspaces.find((w) => w.name === workspaceId);

    setIsWorkspaceLoading(false);
    setWorkspace({
      ...currentWorkspace,
      workspaces,
      workerGroups,
      isWorkspaceLoading,
      workspaceError,
      workspacesError,
      selectedWorkerGroup,
      mutateWorkspaces,
      mutateWorkerGroups,
      setWorkerGroup,
      setWorkspaceId,
    } as IWorkspaceContext);
  }, [
    mutateWorkspaces,
    workspaces,
    workspaceId,
    authorizedOrganizationId,
    workerGroups,
    selectedWorkerGroup,
    mutateWorkerGroups,
    isWorkspaceLoading,
    workspaceError,
    workspacesError,
    setWorkspaceId,
    userPreferences,
  ]);

  useEffect(() => {
    if (!Array.isArray(workerGroups) || workerGroups.length === 0) {
      // This can happen because authorizedOrganizationId is null,
      // or if there are no worker groups returned from the API
      return;
    }

    const defaultWorkerGroup = workerGroups?.find((group) => group.name === DEFAULT_WORKERGROUP_NAME);

    if (defaultWorkerGroup) {
      setSelectedWorkerGroup(defaultWorkerGroup);
    } else {
      setSelectedWorkerGroup(workerGroups.length > 0 ? workerGroups[0] : ({} as WorkerGroup));
    }
  }, [workerGroups]);

  useEffect(() => {
    setWorkspace((prev) => ({
      ...prev,
      selectedWorkerGroup,
    }));
  }, [selectedWorkerGroup]);

  useEffect(() => {
    setWorkspace((prev) => ({
      ...prev,
      capabilities: singleWorkspaceData?.capabilities,
    }));
  }, [singleWorkspaceData]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      if (workspaces?.some((ws) => ws.state !== AccountState.WorkspaceActive)) {
        mutateWorkspaces();
      } else {
        clearInterval(intervalId);
      }
    }, PROVISION_STATE_FETCH_INTERVAL);

    return () => {
      clearInterval(intervalId);
    };
  }, [mutateWorkspaces, workspaces]);

  // Short-interval poll for hibernation pending states so the UI reflects the
  // Temporal workflow result within seconds rather than waiting 60s.
  useEffect(() => {
    const hibernationPendingStates = [AccountState.WorkspaceAsleepRequested, AccountState.WorkspaceWakeRequested];
    const hasHibernationPending = workspaces?.some((ws) =>
      hibernationPendingStates.includes(ws.state as AccountState),
    );

    if (!hasHibernationPending) {
      return;
    }

    const intervalId = setInterval(() => {
      mutateWorkspaces();
    }, HIBERNATION_STATE_FETCH_INTERVAL);

    return () => {
      clearInterval(intervalId);
    };
  }, [mutateWorkspaces, workspaces]);

  return <WorkspaceContext.Provider value={workspace}>{children}</WorkspaceContext.Provider>;
};
